import React, { useEffect, useMemo, useState } from "react";
import { BellRing, X, Settings } from "lucide-react";
import { toast } from "sonner";
import { pushSupport, shouldPrompt, setDecision, subscribeCurrent, autoSubscribeIfReady, isPreDenied } from "@/lib/push";
import { isInstallStepSettled, isStandalone } from "@/lib/install";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";

const COPY = {
  en: {
    kicker: "One quick thing",
    title: "Let Andreas send you wedding updates",
    body: "It\u2019s important for the wedding that you allow notifications. Andreas will remind you about the countdown, RSVP, meal choices, the ceremony start \u2014 and drop the odd Cypriot joke, reee.",
    micro: "You can turn them off any time. No spam, only siga siga.",
    allow: "Allow notifications",
    later: "Not now",
    granted: "Andreas is on the case, koumbare\u2764",
    denied: "No notifications for now \u2014 you can enable them later in your browser settings.",
    error: "Something went wrong on our end. Try again shortly, koumbare.",
    iosInstall: "On iPhone, notifications only work once the app is installed to your home screen. Install first, then Andreas can reach you.",
    unblockTitle: "Notifications are blocked",
    unblockBody: "Chrome is blocking notifications for this site. To hear from Andreas, please unblock them in your browser settings.",
    unblockSteps: [
      "Tap the padlock or three dots in the address bar",
      "Choose Site settings \u2192 Notifications",
      "Set Notifications to Allow, then reload the page",
    ],
    unblockClose: "Close",
  },
  fr: {
    kicker: "Un dernier détail",
    title: "Laisse Andreas t\u2019envoyer les infos du mariage",
    body: "C\u2019est important pour le mariage que tu autorises les notifications. Andreas te rappellera le compte à rebours, le RSVP, les repas, le début de la cérémonie \u2014 et lâchera quelques blagues chypriotes, reee.",
    micro: "Désactivable à tout moment. Pas de spam, juste siga siga.",
    allow: "Autoriser les notifications",
    later: "Plus tard",
    granted: "Andreas s\u2019en occupe, koumbare\u2764",
    denied: "Pas de notifications pour l\u2019instant \u2014 tu peux les réactiver dans les paramètres du navigateur.",
    error: "Petit souci de notre côté. Réessaie tout à l\u2019heure, koumbare.",
    iosInstall: "Sur iPhone, les notifications ne fonctionnent qu\u2019une fois l\u2019app installée à l\u2019écran d\u2019accueil. Installe d\u2019abord, puis Andreas pourra te joindre.",
    unblockTitle: "Notifications bloquées",
    unblockBody: "Chrome bloque les notifications pour ce site. Pour entendre Andreas, débloque-les dans les paramètres du navigateur.",
    unblockSteps: [
      "Touche le cadenas ou les trois points dans la barre d\u2019adresse",
      "Choisis Paramètres du site \u2192 Notifications",
      "Passe Notifications sur Autoriser, puis recharge la page",
    ],
    unblockClose: "Fermer",
  },
  el: {
    kicker: "Ένα γρήγορο θέμα",
    title: "Άφησε τον Andreas να σου στέλνει ειδοποιήσεις για τον γάμο",
    body: "Είναι σημαντικό για τον γάμο να επιτρέψεις τις ειδοποιήσεις. Ο Andreas θα σου θυμίζει την αντίστροφη μέτρηση, το RSVP, το φαγητό, την ώρα της τελετής \u2014 και θα σου πετάει καμιά κυπριακή ατάκα, reee.",
    micro: "Απενεργοποιείς όποτε θες. Χωρίς spam, μόνο σιγά σιγά.",
    allow: "Επιτρέπω ειδοποιήσεις",
    later: "Όχι τώρα",
    granted: "Ο Andreas ανέλαβε, κουμπάρε\u2764",
    denied: "Χωρίς ειδοποιήσεις προς το παρόν \u2014 μπορείς να τις ενεργοποιήσεις από τις ρυθμίσεις του browser.",
    error: "Μικρό πρόβλημα από την πλευρά μας. Δοκίμασε ξανά σε λίγο, κουμπάρε.",
    iosInstall: "Στο iPhone, οι ειδοποιήσεις δουλεύουν μόνο μετά την εγκατάσταση της εφαρμογής στην αρχική οθόνη. Εγκατέστησε πρώτα, μετά ο Andreas θα σε βρίσκει.",
    unblockTitle: "Ειδοποιήσεις μπλοκαρισμένες",
    unblockBody: "Το Chrome μπλοκάρει τις ειδοποιήσεις για αυτόν τον ιστότοπο. Για να ακούσεις τον Andreas, ξεμπλόκαρέ τες από τις ρυθμίσεις του browser.",
    unblockSteps: [
      "Πάτα το λουκέτο ή τις τρεις τελείες στη γραμμή διευθύνσεων",
      "Διάλεξε Ρυθμίσεις ιστότοπου \u2192 Ειδοποιήσεις",
      "Βάλε τις Ειδοποιήσεις σε Επιτρέπονται και ανανέωσε τη σελίδα",
    ],
    unblockClose: "Κλείσιμο",
  },
  pl: {
    kicker: "Jedna szybka rzecz",
    title: "Pozwól Andreasowi wysyłać powiadomienia o ślubie",
    body: "To ważne dla wesela, żebyś zgodził się na powiadomienia. Andreas przypomni ci o odliczaniu, RSVP, wyborze posiłku, początku ceremonii \u2014 i wrzuci parę cypryjskich żartów, reee.",
    micro: "Wyłączysz w każdej chwili. Bez spamu, tylko siga siga.",
    allow: "Zezwól na powiadomienia",
    later: "Może później",
    granted: "Andreas przejął pałeczkę, kumie\u2764",
    denied: "Bez powiadomień na razie \u2014 możesz je włączyć w ustawieniach przeglądarki.",
    error: "Coś nam się nie udało. Spróbuj za chwilę, kumie.",
    iosInstall: "Na iPhonie powiadomienia działają dopiero po zainstalowaniu aplikacji na ekran główny. Zainstaluj najpierw, potem Andreas do ciebie dotrze.",
    unblockTitle: "Powiadomienia zablokowane",
    unblockBody: "Chrome blokuje powiadomienia dla tej strony. Żeby usłyszeć Andreasa, odblokuj je w ustawieniach przeglądarki.",
    unblockSteps: [
      "Dotknij kłódki lub trzech kropek w pasku adresu",
      "Wybierz Ustawienia witryny \u2192 Powiadomienia",
      "Ustaw Powiadomienia na Zezwól i odśwież stronę",
    ],
    unblockClose: "Zamknij",
  },
};

const PushOptInModal = () => {
  const { lang } = useLang();
  const { isAuthed, authReady } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [showDenied, setShowDenied] = useState(false);
  const c = useMemo(() => COPY[lang] || COPY.en, [lang]);

  useEffect(() => {
    if (!authReady || !isAuthed) return;
    if (!pushSupport()) return;

    // 1) Silently subscribe if permission was granted outside our modal.
    autoSubscribeIfReady(lang).catch(() => {});

    // 2) Wait for install decision first, then decide what to show.
    let interval;
    const tryShow = () => {
      if (!isInstallStepSettled()) return;
      // Inside the installed PWA, PwaWelcomeModal owns this flow — stand aside.
      if (isStandalone()) {
        const welcomeState = (() => { try { return localStorage.getItem("em_pwa_welcome_state"); } catch { return null; } })();
        if (welcomeState !== "never" && welcomeState !== "done") {
          // Welcome modal is going to handle it — bail.
          clearInterval(interval);
          return;
        }
      }
      // If perm is pre-denied, offer the recovery dialog once.
      if (isPreDenied()) {
        if (localStorage.getItem("em_push_denied_dismissed") !== "1") {
          setShowDenied(true);
        }
        clearInterval(interval);
        return;
      }
      if (shouldPrompt()) setOpen(true);
      clearInterval(interval);
    };
    const t = setTimeout(() => {
      tryShow();
      if (!open) interval = setInterval(tryShow, 800);
    }, 2800);
    return () => {
      clearTimeout(t);
      if (interval) clearInterval(interval);
    };
  }, [authReady, isAuthed, open, lang]);

  const onAllow = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await subscribeCurrent(lang);
      if (res.ok) {
        toast.success(c.granted);
        setOpen(false);
      } else if (res.permission === "denied") {
        toast.error(c.denied);
        setOpen(false);
      } else {
        // Permission was 'default' — user closed the browser prompt without deciding.
        setOpen(false);
      }
    } catch (e) {
      console.warn("[push] subscribe error", e);
      // Distinguish between real error types so the toast tells the truth.
      if (e && e.kind === "ios-install-required") {
        toast.error(c.iosInstall, { duration: 8000 });
      } else {
        toast.error(c.error);
      }
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const onLater = () => {
    setDecision("later");
    setOpen(false);
  };

  if (!open) {
    if (!showDenied) return null;
    // Denied-permission recovery dialog
    return (
      <div
        className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center bg-foreground/45 backdrop-blur-sm p-4"
        role="dialog"
        aria-modal="true"
        data-testid="push-denied-modal"
      >
        <div className="relative w-full max-w-md bg-background border border-border rounded-2xl shadow-2xl overflow-hidden">
          <button
            type="button"
            onClick={() => { localStorage.setItem("em_push_denied_dismissed", "1"); setShowDenied(false); }}
            className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-md text-foreground/60 hover:text-foreground hover:bg-foreground/5"
            aria-label="Close"
            data-testid="push-denied-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="p-6 sm:p-7">
            <div className="w-12 h-12 rounded-full bg-destructive/15 text-destructive flex items-center justify-center mb-4">
              <Settings className="w-5 h-5" />
            </div>
            <p className="editorial-kicker text-foreground/50">{c.kicker}</p>
            <h2 className="font-serif-display text-2xl sm:text-[26px] leading-tight mt-2">{c.unblockTitle}</h2>
            <p className="text-sm text-foreground/75 mt-3">{c.unblockBody}</p>
            <ol className="mt-4 space-y-2 list-decimal list-inside text-sm text-foreground/80">
              {(c.unblockSteps || []).map((s, i) => <li key={i}>{s}</li>)}
            </ol>
            <div className="mt-6 text-right">
              <button
                type="button"
                onClick={() => { localStorage.setItem("em_push_denied_dismissed", "1"); setShowDenied(false); }}
                className="h-11 px-5 rounded-lg bg-foreground text-background hover:bg-foreground/90 text-[12px] tracking-[0.14em] uppercase"
                data-testid="push-denied-ok"
              >
                {c.unblockClose}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center bg-foreground/40 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid="push-optin-modal"
    >
      <div className="relative w-full max-w-md bg-background border border-border rounded-2xl shadow-2xl overflow-hidden">
        <button
          type="button"
          onClick={onLater}
          className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-md text-foreground/60 hover:text-foreground hover:bg-foreground/5"
          aria-label="Close"
          data-testid="push-optin-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 sm:p-7">
          <div className="w-12 h-12 rounded-full bg-accent/15 text-accent flex items-center justify-center mb-4">
            <BellRing className="w-5 h-5" />
          </div>
          <p className="editorial-kicker text-foreground/50" data-testid="push-optin-kicker">{c.kicker}</p>
          <h2 className="font-serif-display text-2xl sm:text-3xl leading-tight mt-2" data-testid="push-optin-title">{c.title}</h2>
          <p className="text-sm sm:text-[15px] leading-relaxed text-foreground/75 mt-3" data-testid="push-optin-body">{c.body}</p>
          <p className="text-[12px] italic text-foreground/50 mt-3">{c.micro}</p>

          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <button
              type="button"
              onClick={onLater}
              className="h-11 px-5 rounded-lg border border-border text-foreground/70 hover:text-foreground hover:border-foreground/40 transition-colors text-[13px] tracking-[0.14em] uppercase"
              data-testid="push-optin-later"
            >
              {c.later}
            </button>
            <button
              type="button"
              onClick={onAllow}
              disabled={busy}
              className="h-11 px-5 rounded-lg bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60 transition-colors text-[13px] tracking-[0.14em] uppercase inline-flex items-center justify-center gap-2"
              data-testid="push-optin-allow"
            >
              <BellRing className="w-4 h-4" />
              {c.allow}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushOptInModal;
